/**
 * plugins/index.ts
 *
 * Automatically included in `./src/main.ts`
 */

import type { Router } from "vue-router"
import { createPinia } from "pinia"
import router from "@/router"
import { init } from "@sentry/capacitor"
import {
  browserProfilingIntegration,
  browserTracingIntegration,
  replayIntegration
} from "@sentry/vue"
import "@/plugins/moment"
import { VueDatePicker } from "@vuepic/vue-datepicker"
import "@vuepic/vue-datepicker/dist/main.css"
import "@/assets/css/tailwind.css"
import type { App, Component } from "vue"
import FontAwesomeIcon from "@/plugins/fontawesome"
import { postBootMobileApp } from "@/app-startup"
import { initApplicationInsights } from "@/services/application-insights-service"
import { startUiBlockTracking } from "@/services/ui-block-tracking-service"
import { isAcceptanceEnvironment, isProductionEnvironment } from "@/helper/app/browser-helper"
import { useAuthStore } from "@/store/auth.store"
import { isSessionRecordingActive } from "@/helper/app/session-recording-state"
import type { AxiosError } from "axios"
import { clerkPlugin } from "@clerk/vue"
import { nlNL } from "@clerk/localizations"
import { Capacitor } from "@capacitor/core"
import { version } from "../../package.json"
import ui from "@nuxt/ui/vue-plugin"
import { LOGIN_VERIFY_PATH } from "@/router/auth-paths"

//use dev key when on localhost else use prd key
const publishableKey = isProductionEnvironment()
  ? import.meta.env.VITE_CLERK_PUBLISHABLE_PRD_KEY
  : import.meta.env.VITE_CLERK_PUBLISHABLE_DEV_KEY

const pinia = createPinia()

function getEnvironment() {
  if (isAcceptanceEnvironment()) {
    return "acceptance"
  }

  if (isProductionEnvironment()) {
    return "master"
  }

  // Dev environments are addressed by ticket and only by ticket:
  // <ticket>.pnts.dev, one origin, Sentry environment PNTS-<ticket> to match
  // the backend's (which takes it from DB_NAME). Hostnames are always
  // lowercase. Anything else - localhost, a native shell, the raw Coolify
  // origin used to debug a broken ticket lookup - is development.
  const ticket = window.location.hostname.match(/^(\d+)\.pnts\.dev$/)
  if (ticket) {
    return `PNTS-${ticket[1]}`
  }

  return "development"
}

function initSentry(app: App, router: Router) {
  // Local/unmatched hosts (e.g. localhost) still need Sentry initialized so
  // the Replay integration exists for on-demand session recording.
  const environment = getEnvironment()

  if (environment !== undefined) {
    init(
      {
        dsn: "https://3f03e32dd4c8240fb38ac7d6d176f5b4@o4506966444277760.ingest.us.sentry.io/4509723122663424",
        release: `${version}`,
        siblingOptions: {
          vueOptions: {
            app,
            attachProps: true,
            attachErrorHandler: true
          }
        },
        integrations: [
          browserTracingIntegration({ router }),
          // Session Replay (rrweb) retains a node mirror that pins detached DOM
          // and accumulates un-removed stylesheet load listeners, causing severe
          // memory/listener growth on the heavy web DOM. The integration is
          // registered everywhere so it can be started on demand, but it only
          // auto-records on native (Capacitor); on web it stays dormant
          // (replaysSessionSampleRate 0 below) until a user starts it manually
          // via the navigation menu.
          replayIntegration({
            maskAllText: false,
            maskAllInputs: false,
            blockAllMedia: false,
            networkDetailAllowUrls: [
              "capacitor://localhost",
              window.location.origin,
              /^https:\/\/api\d*\.peanuts\.nl\/.*$/,
              /^https:\/\/clerk\.peanuts\.nl/,
              /^https:\/\/peanutsstorage\.blob\.core\.windows\.net\/files\/.*$/
            ],
            networkCaptureBodies: true,
            networkRequestHeaders: ["authorization"]
          }),
          // Browser (UI) profiling captures sampled CPU call stacks via the JS
          // Self-Profiling API and renders flame graphs in Sentry — the SDK
          // equivalent of taking a snapshot in the DevTools Performance tab.
          // Like Session Replay it stays dormant: with profileLifecycle
          // "manual" (set below) it only runs after Sentry.profiler.startProfiler()
          // is called from the navigation menu's "Sessie opnemen" action.
          // NOTE: the JS Self-Profiling API requires the
          // `Document-Policy: js-profiling` response header (Chromium only);
          // without it window.Profiler is unavailable and this no-ops.
          browserProfilingIntegration()
        ],
        // drop debug-level (i.e. verbose) breadcrumbs
        // beforeBreadcrumb(breadcrumb) {
        //   if (breadcrumb.category === "console" && breadcrumb.level === "debug") {
        //     return null
        //   }
        //   return breadcrumb
        // },
        environment,
        // Tracing is dormant by default (returns 0), matching the on-demand
        // Replay/profiling design below. While a support recording is active it
        // returns 1.0 so the session's navigation/interaction transactions are
        // captured and attached to the Session Replay — that is what populates
        // the replay's performance metrics (with tracing off the replay has
        // none). Note: during a recording, `sentry-trace`/`baggage` headers are
        // propagated to the `tracePropagationTargets` below.
        tracesSampler: () => (isSessionRecordingActive() ? 1.0 : 0.0),
        tracePropagationTargets: [
          "azurewebsites.net",
          /^https:\/\/.*\.azurewebsites\.net/,
          /^https:\/\/api\d*\.peanuts\.nl\/.*$/
        ],
        // @ts-expect-error Deprecated property
        replaysSessionSampleRate: 1.0,
        replaysOnErrorSampleRate: 1.0,
        // Continuous profiling: keep the session eligible for profiling (1.0),
        // but in "manual" mode nothing is captured until startProfiler() runs.
        profileSessionSampleRate: 1.0,
        profileLifecycle: "manual",
        beforeSend(event, hint) {
          const error = hint && hint.originalException
          // Axios errors often have a response object
          if ((error as AxiosError)?.response?.status) {
            const status = (error as AxiosError)?.response?.status
            if (status === 401 || status === 423) {
              return null // Ignore these errors
            }
          }
          // Network errors: Axios sets error.code for network issues
          const axiosCode = (error as AxiosError)?.code
          if (axiosCode) {
            const networkErrorCodes = [
              "ECONNABORTED",
              "ENOTFOUND",
              "ECONNREFUSED",
              "ECONNRESET",
              "ETIMEDOUT",
              "EAI_AGAIN"
            ]
            if (networkErrorCodes.includes(axiosCode)) {
              return null // Ignore network errors
            }
          }
          // Optionally, ignore errors by message (HTTP and WebSocket)
          const errorMessage = (error as AxiosError)?.message
          if (errorMessage) {
            // WebSocket network error patterns
            const wsNetworkPatterns = [
              /ECONNREFUSED/i,
              /ENOTFOUND/i,
              /ECONNRESET/i,
              /ETIMEDOUT/i,
              /EAI_AGAIN/i,
              /abnormal/i,
              /timeout/i,
              /network/i,
              /WebSocket closed abnormally/i,
              /WebSocket error:.*(ECONNREFUSED|ENOTFOUND|ECONNRESET|ETIMEDOUT|EAI_AGAIN|abnormal|timeout|network)/i
            ]
            if (
              errorMessage.includes("Network Error") ||
              errorMessage.includes("Request failed with status code 401") ||
              errorMessage.includes("Request failed with status code 423") ||
              wsNetworkPatterns.some((pattern) => pattern.test(errorMessage))
            ) {
              return null
            }
          }
          return event
        }
      },
      init
    )
  }
}

const nlNLCustom = {
  ...nlNL,
  unstable__errors: {
    form_identifier_not_found:
      "Geen account gevonden met deze gegevens, je moet je eerst registreren om in te kunnen loggen."
  }
}

export function registerPlugins(app: App) {
  initApplicationInsights()
  startUiBlockTracking({
    getTenant: () => useAuthStore().activeUser?.activeTenant?.name?.trim() || "unknown",
    getRoute: () => router.currentRoute.value.fullPath
  })
  initSentry(app, router)

  app
    .use(pinia)
    .use(router)
    .use(ui)
    .use(clerkPlugin, {
      publishableKey,
      localization: nlNLCustom,
      // The e-mail login link opens <signInUrl>#/verify in a new tab. Point it at a
      // page that only verifies the link, so that tab never boots the app (PNTS: two
      // signed-in tabs after clicking the link).
      signInUrl: `${window.location.origin}${LOGIN_VERIFY_PATH}`
    })
    .component("VueDatePicker", VueDatePicker)
    // FontAwesomeIcon 7.3.0 produces a union type that is too complex to represent, so we cast it to Component
    .component("FontAwesomeIcon", FontAwesomeIcon as Component)

  router.isReady().then(() => {
    // Waiting for the router to be ready prevents race conditions when returning from a loginRedirect or acquireTokenRedirect
    app.mount("#app")
    if (Capacitor.isNativePlatform()) {
      postBootMobileApp().then(() => {
        console.log("Post-booted mobile app...")
      })
    }
  })
}
